import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    ListGroup,
    ListGroupItem,
    Nav,
    NavItem,
    NavLink,
    Progress,
    Row,
    Spinner,
} from "reactstrap";
import api from "../services/api";
import Header from "../components/public/Header";
import Footer from "../components/public/Footer";

const getTeamName = (entry) => {
    if (typeof entry === "string") {
        return entry;
    }

    return entry?.team?.name || entry?.team_name || entry?.team_display || "Unnamed Team";
};

const getPlayerNameFromEntry = (player) => {
    if (typeof player === "string") {
        return player.trim();
    }

    if (!player || typeof player !== "object") {
        return "";
    }

    const firstName = typeof player.first_name === "string" ? player.first_name.trim() : "";
    const lastName = typeof player.last_name === "string" ? player.last_name.trim() : "";
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) {
        return fullName;
    }

    const fallbacks = [player.name, player.full_name, player.display_name];
    const fallbackName = fallbacks.find((value) => typeof value === "string" && value.trim().length > 0);
    return fallbackName ? fallbackName.trim() : "";
};

const getTeamPlayerNames = (entry) => {
    const candidates = [
        entry?.players,
        entry?.team?.players,
        entry?.team_players,
        entry?.members,
        entry?.team?.members,
        entry?.player_names,
    ];

    const players = candidates.find((candidate) => Array.isArray(candidate));

    if (!players) {
        return [];
    }

    return players
        .map(getPlayerNameFromEntry)
        .filter(Boolean);
};

const getAverageScore = (entry) => {
    if (typeof entry?.average_score === "number") {
        return entry.average_score;
    }

    return null;
};

const getTotalScore = (entry) => {
    if (typeof entry?.total_score === "number") {
        return entry.total_score;
    }

    return null;
};

const getRoundsPlayed = (entry) => {
    if (typeof entry?.rounds_played === "number") {
        return entry.rounds_played;
    }

    return null;
};

const getRank = (entry) => {
    if (typeof entry?.rank === "number") {
        return entry.rank;
    }

    return null;
};

const getScores = (payload) => {
    if (!Array.isArray(payload?.scores)) {
        return [];
    }

    return payload.scores;
};

// const TARGET_ROUNDS = 7; // Replaced by leagueWeeks in state
const TOP_WEEKLY_SCORES_LIMIT = 10;
const SCORE_PILL_STYLE = {
    width: "95%",
    maxWidth: "72px",
    fontSize: "clamp(0.72rem, 2.7vw, 0.875rem)",
};

const getRoundsProgressValue = (roundsPlayed, leagueWeeks) => {
    const safeRounds = Math.max(0, roundsPlayed ?? 0);
    const cappedRounds = Math.min(safeRounds, leagueWeeks ?? 0);
    if (!leagueWeeks || leagueWeeks <= 0) return 0;
    return Math.round((cappedRounds / leagueWeeks) * 100);
};

const getScoreClassName = (score) => {
    if (typeof score !== "number") {
        return "text-muted";
    }

    if (score < 0) {
        return "text-danger";
    }

    if (score > 0) {
        return "text-success";
    }

    return "text-secondary";
};

const formatAverageScore = (score) => {
    if (typeof score !== "number") {
        return "-";
    }

    if (score === 0) {
        return "E";
    }

    return score.toFixed(1);
};

const formatScoreValue = (score) => {
    if (typeof score !== "number") {
        return "-";
    }

    if (score === 0) {
        return "E";
    }

    return score;
};

const getLeagueName = (payload) => {
    return payload?.league_name || payload?.league?.title || payload?.league_title || "League";
};

const LeagueScores = () => {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [scores, setScores] = useState([]);
    const [leagueName, setLeagueName] = useState("League");
    const [leagueWeeks, setLeagueWeeks] = useState(0);
    const [showLeaderboardTab, setShowLeaderboardTab] = useState(true);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: "rank", direction: "asc" });

    const requestSort = (key) => {
        setSortConfig((current) => {
            if (current.key === key) {
                return {
                    key,
                    direction: current.direction === "asc" ? "desc" : "asc",
                };
            }

            return {
                key,
                direction: key === "team" ? "asc" : "desc",
            };
        });
    };

    const handleMobileSortKeyChange = (event) => {
        const key = event.target.value;
        setSortConfig({
            key,
            direction: "asc",
        });
    };

    const toggleSortDirection = () => {
        setSortConfig((current) => ({
            ...current,
            direction: current.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) {
            return "";
        }

        return sortConfig.direction === "asc" ? " \u2191" : " \u2193";
    };

    useEffect(() => {
        document.title = "League Scores - Reserve2Day";
    }, []);

    useEffect(() => {
        api.get("/get-org-details/")
            .then((response) => {
                setOrganization(response.data);
            })
            .catch(() => {
                setOrganization(null);
            });
    }, []);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                const response = await api.get(`/league/${leagueId}/leaderboard`);
                setLeagueName(getLeagueName(response.data));
                const leaderboardEnabled = response.data?.show_leaderboard
                    ?? response.data?.league?.show_leaderboard
                    ?? true;
                setShowLeaderboardTab(Boolean(leaderboardEnabled));
                const leaderboardTeams = Array.isArray(response.data?.teams)
                    ? response.data.teams
                    : (Array.isArray(response.data) ? response.data : []);
                const leaderboardScores = getScores(response.data);
                setLeaderboard(leaderboardTeams);
                setScores(leaderboardScores);
                const weeks = response.data?.league_weeks ?? 0;
                setLeagueWeeks(weeks);
            } catch (error) {
                console.error("Error loading league leaderboard:", error);
                setLeagueName("League");
                setLeaderboard([]);
                setScores([]);
                setLeagueWeeks(0);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, [leagueId]);

    const weeklyScoresByTeam = useMemo(() => {
        const byTeam = new Map();

        scores.forEach((entry) => {
            const teamName = getTeamName(entry);

            if (!byTeam.has(teamName)) {
                byTeam.set(teamName, []);
            }

            byTeam.get(teamName).push(entry);
        });

        byTeam.forEach((teamScores) => {
            teamScores.sort((a, b) => {
                const aWeek = typeof a?.week === "number" ? a.week : Number.POSITIVE_INFINITY;
                const bWeek = typeof b?.week === "number" ? b.week : Number.POSITIVE_INFINITY;
                return aWeek - bWeek;
            });
        });

        return byTeam;
    }, [scores]);

    const sortedTeams = useMemo(() => {
        const mappedTeams = leaderboard
            .map((entry) => ({
                name: getTeamName(entry),
                playerNames: getTeamPlayerNames(entry),
                rank: getRank(entry),
                averageScore: getAverageScore(entry),
                totalScore: getTotalScore(entry),
                roundsPlayed: getRoundsPlayed(entry),
                weeklyScores: weeklyScoresByTeam.get(getTeamName(entry)) || [],
            }));

        const defaultComparer = (a, b) => {
                const aRank = a.rank ?? Number.POSITIVE_INFINITY;
                const bRank = b.rank ?? Number.POSITIVE_INFINITY;

                if (aRank !== bRank) {
                    return aRank - bRank;
                }

                const aAverage = a.averageScore ?? Number.POSITIVE_INFINITY;
                const bAverage = b.averageScore ?? Number.POSITIVE_INFINITY;

                if (aAverage !== bAverage) {
                    return aAverage - bAverage;
                }

                const aRounds = a.roundsPlayed ?? 0;
                const bRounds = b.roundsPlayed ?? 0;

                if (aRounds !== bRounds) {
                    return bRounds - aRounds;
                }

                return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
            };

        const sorted = [...mappedTeams].sort((a, b) => {
            const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;

            if (sortConfig.key === "average") {
                const aValue = a.averageScore ?? Number.POSITIVE_INFINITY;
                const bValue = b.averageScore ?? Number.POSITIVE_INFINITY;
                if (aValue !== bValue) {
                    return (aValue - bValue) * directionMultiplier;
                }
            }

            if (sortConfig.key === "total") {
                const aValue = a.totalScore ?? Number.POSITIVE_INFINITY;
                const bValue = b.totalScore ?? Number.POSITIVE_INFINITY;
                if (aValue !== bValue) {
                    return (aValue - bValue) * directionMultiplier;
                }
            }

            if (sortConfig.key === "team") {
                const result = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
                if (result !== 0) {
                    return result * directionMultiplier;
                }
            }

            if (sortConfig.key === "rounds") {
                const aValue = a.roundsPlayed ?? Number.NEGATIVE_INFINITY;
                const bValue = b.roundsPlayed ?? Number.NEGATIVE_INFINITY;
                if (aValue !== bValue) {
                    return (aValue - bValue) * directionMultiplier;
                }
            }

            if (sortConfig.key === "rank") {
                const aValue = a.rank ?? Number.POSITIVE_INFINITY;
                const bValue = b.rank ?? Number.POSITIVE_INFINITY;
                if (aValue !== bValue) {
                    return (aValue - bValue) * directionMultiplier;
                }
            }

            return defaultComparer(a, b);
        });

        return sorted;
    }, [leaderboard, weeklyScoresByTeam, sortConfig]);

    const topWeeklyScores = useMemo(() => {
        return scores
            .map((entry) => ({
                teamName: getTeamName(entry),
                week: entry?.week,
                notes: entry?.notes,
                score: entry?.score,
                leagueWeekId: entry?.league_week_id,
            }))
            .filter((entry) => typeof entry.score === "number")
            .sort((a, b) => {
                if (a.score !== b.score) {
                    return a.score - b.score;
                }

                const aWeek = typeof a.week === "number" ? a.week : Number.POSITIVE_INFINITY;
                const bWeek = typeof b.week === "number" ? b.week : Number.POSITIVE_INFINITY;

                if (aWeek !== bWeek) {
                    return aWeek - bWeek;
                }

                return a.teamName.localeCompare(b.teamName, undefined, { sensitivity: "base" });
            })
                .slice(0, TOP_WEEKLY_SCORES_LIMIT);
            }, [scores]);

        const topScoreByWeek = useMemo(() => {
            const groupedByWeek = new Map();

            scores.forEach((entry) => {
                const week = entry?.week;
                const score = entry?.score;

                if (typeof week !== "number" || typeof score !== "number") {
                    return;
                }

                if (!groupedByWeek.has(week)) {
                    groupedByWeek.set(week, []);
                }

                groupedByWeek.get(week).push({
                    teamName: getTeamName(entry),
                    score,
                    notes: entry?.notes || "",
                });
            });

            const maxWeek = groupedByWeek.size > 0
                ? Math.max(...Array.from(groupedByWeek.keys()))
                : 0;

            return Array.from({ length: maxWeek }, (_, index) => {
                const week = index + 1;
                const weekEntries = groupedByWeek.get(week) || [];

                if (weekEntries.length === 0) {
                    return {
                        week,
                        lowScore: null,
                        ties: [],
                    };
                }

                const lowScore = Math.min(...weekEntries.map((item) => item.score));
                const ties = weekEntries
                    .filter((item) => item.score === lowScore)
                    .sort((a, b) => a.teamName.localeCompare(b.teamName, undefined, { sensitivity: "base" }));

                const weekNotes = weekEntries.find((item) => item.notes)?.notes || "";

                return {
                    week,
                    lowScore,
                    ties,
                    weekNotes,
                };
            });
        }, [scores]);

    if (loading) {
        return (
            <>
                <Header organization={organization} showBanner={false} />
                <Container className="text-center mt-5">
                    <Spinner color="primary" />
                    <p className="mt-3">Loading teams...</p>
                </Container>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header organization={organization} showBanner={false} />
            <Container className="mt-4 mb-5" id="league-scores-top">
                <h2 className="text-start mb-3">{leagueName}</h2>
                <Nav tabs className="mb-3 league-route-tabs">
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/league/${leagueId}/schedule`)}
                        >
                            Full Schedule
                        </NavLink>
                    </NavItem>
                    {showLeaderboardTab && (
                        <NavItem>
                            <NavLink className="active" style={{ cursor: "pointer" }}>
                                Leaderboard
                            </NavLink>
                        </NavItem>
                    )}
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/league/${leagueId}/upcoming`)}
                        >
                            Upcoming
                        </NavLink>
                    </NavItem>
                </Nav>
                <Row className="d-lg-none g-2 mb-3">
                    <Col xs={6}>
                        <Button tag="a" href="#top-weekly-scores" color="outline-secondary" size="sm" className="w-100">
                            Top {TOP_WEEKLY_SCORES_LIMIT} Scores
                        </Button>
                    </Col>
                    <Col xs={6}>
                        <Button tag="a" href="#top-score-by-week" color="outline-secondary" size="sm" className="w-100">
                            Top Scores by Week
                        </Button>
                    </Col>
                </Row>
                <Row className="justify-content-center align-items-start g-3">
                    <Col xs={12} xl={8}>
                        <Card className="shadow-sm" id="leaderboard-section">
                            <CardHeader className="fw-semibold text-center">Leaderboard</CardHeader>
                            <CardBody>
                                {sortedTeams.length === 0 ? (
                                    <p className="text-center text-muted mb-0">No teams found for this league.</p>
                                ) : (
                                    <>
                                        <Row className="d-md-none g-2 mb-3">
                                            <Col xs={8}>
                                                <Input
                                                    type="select"
                                                    bsSize="sm"
                                                    value={sortConfig.key}
                                                    onChange={handleMobileSortKeyChange}
                                                >
                                                    <option value="rank">Sort: Rank</option>
                                                    <option value="average">Sort: Average</option>
                                                    <option value="total">Sort: Total</option>
                                                    <option value="team">Sort: Team</option>
                                                </Input>
                                            </Col>
                                            <Col xs={4}>
                                                <Button outline color="secondary" size="sm" className="w-100" onClick={toggleSortDirection}>
                                                    {sortConfig.direction === "asc" ? "Asc \u2191" : "Desc \u2193"}
                                                </Button>
                                            </Col>
                                        </Row>
                                        <Row className="px-3 pb-2 text-muted small fw-semibold d-none d-md-flex g-1">
                                            <Col md={1} className="text-center">
                                                <Button color="link" className="p-0 text-decoration-none text-muted fw-semibold" onClick={() => requestSort("rank")}>
                                                    Rank{getSortIndicator("rank")}
                                                </Button>
                                            </Col>
                                            <Col md={1} className="text-center">
                                                <Button color="link" className="p-0 text-decoration-none text-muted fw-semibold" onClick={() => requestSort("average")}>
                                                    Ave{getSortIndicator("average")}
                                                </Button>
                                            </Col>
                                            <Col md={1} className="text-center">
                                                <Button color="link" className="p-0 text-decoration-none text-muted fw-semibold" onClick={() => requestSort("total")}>
                                                    Total{getSortIndicator("total")}
                                                </Button>
                                            </Col>
                                            <Col md={3} className="text-center">
                                                <Button color="link" className="p-0 text-decoration-none text-muted fw-semibold" onClick={() => requestSort("team")}>
                                                    Team{getSortIndicator("team")}
                                                </Button>
                                            </Col>
                                            <Col md={6} className="text-center">Weekly Scores</Col>
                                        </Row>
                                        <ListGroup flush>
                                            {sortedTeams.map((team, index) => (
                                                <ListGroupItem key={`${team.name}-${index}`} className="py-3">
                                                    <Row className="align-items-start g-1">
                                                        <Col xs={12} md={6}>
                                                            <Row className="align-items-start g-1">
                                                                <Col xs={12} md={2} className="text-md-center">
                                                                    <span className="text-muted d-md-none me-2">Rank:</span>
                                                                    <span className="badge rounded-pill text-bg-dark">{team.rank ?? "-"}</span>
                                                                </Col>
                                                                <Col xs={12} md={2} className="text-md-center">
                                                                    <span className="text-muted d-md-none me-2">Ave:</span>
                                                                    <span
                                                                        className={`badge rounded-pill border bg-light d-inline-block text-center ${getScoreClassName(team.averageScore)}`}
                                                                        style={SCORE_PILL_STYLE}
                                                                    >
                                                                        {formatAverageScore(team.averageScore)}
                                                                    </span>
                                                                </Col>
                                                                <Col xs={12} md={2} className="text-md-center">
                                                                    <span className="text-muted d-md-none me-2">Total:</span>
                                                                    <span
                                                                        className={`badge rounded-pill border bg-light d-inline-block text-center ${getScoreClassName(team.totalScore)}`}
                                                                        style={SCORE_PILL_STYLE}
                                                                    >
                                                                        {formatScoreValue(team.totalScore)}
                                                                    </span>
                                                                </Col>
                                                                <Col xs={12} md={6}>
                                                                    <div className="fw-semibold">{team.name}</div>
                                                                    {team.playerNames.length > 0 && (
                                                                        <div className="small text-muted mt-1">
                                                                            {team.playerNames.map((playerName) => (
                                                                                <div key={`${team.name}-${playerName}`}>{playerName}</div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </Col>
                                                            </Row>
                                                            <Row className="g-1 mt-2">
                                                                <Col xs={12}>
                                                                    <Progress
                                                                        className="mt-1"
                                                                        value={getRoundsProgressValue(team.roundsPlayed, leagueWeeks)}
                                                                        color="success"
                                                                        aria-label={`Rounds played ${Math.max(0, team.roundsPlayed ?? 0)} of ${leagueWeeks}`}
                                                                        style={{ height: "0.6rem" }}
                                                                    />
                                                                    <div className="small text-muted text-center mt-1">
                                                                        Progress {Math.max(0, team.roundsPlayed ?? 0)}/{leagueWeeks}
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                        <Col xs={12} md={6}>
                                                            {team.weeklyScores.length > 0 && (
                                                                <div className="mt-0 text-end">
                                                                    <ul className="mt-0 mb-0 ps-3 small text-muted text-start d-inline-block">
                                                                        {team.weeklyScores.map((weekScore) => (
                                                                            <li key={weekScore.league_week_id ?? `${team.name}-week-${weekScore.week}`}>
                                                                                Week {weekScore.week ?? "-"}: <span className={getScoreClassName(weekScore.score)}>{formatScoreValue(weekScore.score)}</span>
                                                                                {weekScore.notes ? ` (${weekScore.notes})` : ""}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </ListGroupItem>
                                            ))}
                                        </ListGroup>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                        <div className="d-lg-none mt-2 text-end">
                            <Button tag="a" href="#league-scores-top" color="link" size="sm" className="p-0 text-decoration-none">
                                Back to top
                            </Button>
                        </div>
                    </Col>

                    <Col xs={12} xl={4}>
                        <Card className="shadow-sm" id="top-weekly-scores">
                            <CardHeader className="fw-semibold text-center">Top {TOP_WEEKLY_SCORES_LIMIT} Scores</CardHeader>
                            <CardBody>
                                {topWeeklyScores.length === 0 ? (
                                    <p className="text-center text-muted mb-0">No weekly scores available.</p>
                                ) : (
                                    <ListGroup flush>
                                        {topWeeklyScores.map((entry, index) => (
                                            <ListGroupItem key={`${entry.teamName}-${entry.leagueWeekId ?? "na"}-${entry.week ?? "na"}-${index}`} className="px-0">
                                                <div className="d-flex justify-content-between align-items-start gap-2">
                                                    <div>
                                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                                            <span className="badge rounded-pill text-bg-dark">{index + 1}</span>
                                                            <span>{entry.teamName}</span>
                                                        </div>
                                                        <div className="small text-muted">
                                                            Week {entry.week ?? "-"}
                                                            {entry.notes ? ` - ${entry.notes}` : ""}
                                                        </div>
                                                    </div>
                                                    <span className={`badge rounded-pill border bg-light ${getScoreClassName(entry.score)}`}>
                                                        {formatScoreValue(entry.score)}
                                                    </span>
                                                </div>
                                            </ListGroupItem>
                                        ))}
                                    </ListGroup>
                                )}
                            </CardBody>
                        </Card>
                        <div className="d-lg-none mt-2 text-end">
                            <Button tag="a" href="#league-scores-top" color="link" size="sm" className="p-0 text-decoration-none">
                                Back to top
                            </Button>
                        </div>

                        <Card className="shadow-sm mt-3" id="top-score-by-week">
                            <CardHeader className="fw-semibold text-center">Top Scores by Week</CardHeader>
                            <CardBody>
                                {topScoreByWeek.length === 0 ? (
                                    <p className="text-center text-muted mb-0">No weekly score data available.</p>
                                ) : (
                                    <ListGroup flush>
                                        {topScoreByWeek.map((weekItem) => (
                                            <ListGroupItem key={`top-week-${weekItem.week}`} className="px-0">
                                                <div className="d-flex justify-content-between align-items-start gap-2">
                                                    <div>
                                                        <div className="fw-semibold">Week {weekItem.week}
                                                        {weekItem.weekNotes && (
                                                            <span>: {weekItem.weekNotes}</span>
                                                        )}</div>
                                                        {weekItem.ties.length === 0 ? (
                                                            <div className="small text-muted">No scores</div>
                                                        ) : (
                                                            weekItem.ties.map((tie, tieIndex) => (
                                                                <div className="small text-muted" key={`${weekItem.week}-${tie.teamName}-${tieIndex}`}>
                                                                    {tie.teamName}
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                    <span className={`badge rounded-pill border bg-light ${getScoreClassName(weekItem.lowScore)}`}>
                                                        {formatScoreValue(weekItem.lowScore)}
                                                    </span>
                                                </div>
                                            </ListGroupItem>
                                        ))}
                                    </ListGroup>
                                )}
                            </CardBody>
                        </Card>
                        <div className="d-lg-none mt-2 text-end">
                            <Button tag="a" href="#league-scores-top" color="link" size="sm" className="p-0 text-decoration-none">
                                Back to top
                            </Button>
                        </div>
                    </Col>
                </Row>

                <div className="text-center mt-4">
                    <Button outline color="secondary" onClick={() => navigate(`/league/${leagueId}/schedule`)}>
                        Back to League Schedule
                    </Button>
                </div>
            </Container>
            <Footer />
        </>
    );
};

export default LeagueScores;